import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import SpeciesSearchWidget from './SpeciesSearchWidget';
import SpeciesGalleryModal from './SpeciesGalleryModal';
import { speciesHeader, speciesReferencesBox, speciesClassificationBox } from './SpeciesInfoBoxes';
import extractPostImages from '../utils/extractPostImages';
import authorDisplayName from '../utils/authorDisplayName';
// champis-forum's "Modifier" (components/EditPostButton): the edit composer
// for the description's text, for whoever may edit it - looked up at render
// time through flarum.reg, an extension's code not importable by another.
function editPostButton(post) {
  const EditPostButton = window.flarum.reg.get('champis-forum', 'forum/components/EditPostButton');
  return EditPostButton ? m(EditPostButton, {
    post
  }) : null;
}

/**
 * /description/:id - a single "descriptions"-tagged discussion, presented
 * as a standalone species writeup rather than a regular discussion (see
 * DescriptionsPage.js for the listing this is linked from), matching
 * champis.net/description/:id: same header/référentiels/systématique boxes
 * as SpeciesPage (see SpeciesInfoBoxes.js), with the discussion's own first
 * post as the body instead of a discussion list. "Modifier" after the
 * author, for whoever may edit it: its photos and text are read again from
 * the post whenever its HTML changes (see body()).
 */
export default class DescriptionPage extends Page {
  discussion = null;
  species = null;
  notFound = false;
  images = [];
  bodyHtml = '';
  // The post HTML images/bodyHtml were read from.
  bodySource = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.discussion = null;
    this.species = null;
    this.notFound = false;
    this.images = [];
    this.bodyHtml = '';
    this.bodySource = null;
    this.load(m.route.param('id'));
  }
  load(id) {
    this.discussion = null;
    this.species = null;
    this.notFound = false;
    this.images = [];
    this.bodyHtml = '';
    this.bodySource = null;
    app.store.find('discussions', id, {
      include: 'firstPost,firstPost.user,user'
    }).then(discussion => {
      const entries = discussion.species ? discussion.species() : [];
      const primary = entries && entries[0];
      if (!primary) {
        this.notFound = true;
        m.redraw();
        return;
      }
      this.discussion = discussion;
      m.redraw();

      // primary.gbifId is already the *accepted* id (species() resolves
      // synonyms - see the `species` field's own docblock in extend.php),
      // so querying by that would never populate this.species' own
      // synonym* fields at all: synonymId is the one place the
      // discussion's actual tagged id survives, only present when it was
      // itself a synonym - needed for speciesHeader()'s synonymSuffix
      // below to have anything to show.
      return app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/species/' + (primary.synonymId ?? primary.gbifId) + '?includeReferences=true&vernacularName=true'
      }).then(response => {
        this.species = {
          id: Number(response.data.id),
          ...response.data.attributes
        };
        app.setTitle(this.species.scientificName);
        m.redraw();
      });
    }).catch(() => {
      this.notFound = true;
      m.redraw();
    });
  }

  // The post's photos (the gallery) and its text without them - read again
  // once it's edited.
  body(post) {
    const html = post.contentHtml() || '';
    if (html !== this.bodySource) {
      const {
        images,
        bodyHtml
      } = extractPostImages(html);
      this.images = images;
      this.bodyHtml = bodyHtml;
      this.bodySource = html;
    }
  }
  view() {
    const post = this.discussion && this.discussion.firstPost();
    if (post) this.body(post);
    return m("div", {
      className: "Page SpeciesPage DescriptionPage"
    }, m("div", {
      className: "container"
    }, this.notFound ? m("p", {
      className: "SpeciesPage-error"
    }, app.translator.trans('champis-species.forum.description_page.not_found')) : !this.species ? m(LoadingIndicator, null) : speciesHeader(this.species, {
      linkTitle: true,
      synonymSuffix: true
    }), this.species && m("div", {
      className: "Page-container"
    }, m("div", {
      className: "Page-content"
    }, m("p", {
      className: "DescriptionPage-author"
    }, app.translator.trans('champis-species.forum.gallery.by', {
      name: authorDisplayName(this.discussion.user() || null)
    }), ' ', editPostButton(post)), this.images.length > 0 && this.gallery(), post ? m("div", {
      className: "Post-body"
    }, m.trust(this.bodyHtml)) : m(LoadingIndicator, null)), m("div", {
      className: "Page-sidebar"
    }, m(SpeciesSearchWidget, null), speciesReferencesBox(this.species), speciesClassificationBox(this.species)))));
  }
  gallery() {
    return m("div", {
      className: "DescriptionGallery"
    }, this.images.map((photo, index) => m("a", {
      key: photo.id,
      className: "DescriptionGallery-item",
      href: "#",
      title: photo.baseName,
      onclick: e => {
        e.preventDefault();
        app.modal.show(SpeciesGalleryModal, {
          photos: this.images,
          index
        });
      }
    }, m("img", {
      src: photo.thumbnailUrl,
      alt: photo.baseName,
      loading: "lazy"
    }))));
  }
}
flarum.reg.add('champis-species', 'forum/components/DescriptionPage', DescriptionPage);